import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Alert,
  Spinner,
  IconButton,
  Tooltip,
} from '@material-tailwind/react';
import { DocumentArrowDownIcon, ArrowPathIcon, XMarkIcon, BoltIcon } from '@heroicons/react/24/outline';

import SectionList from './SectionList';
import DelegationPanel from './DelegationPanel';
import DynamicSectionsEditor from './DynamicSectionsEditor';
import PreviewPane from './PreviewPane';
import SubjectAircraftPanel from './SubjectAircraftPanel';
import AnalysisConfigPanel from './AnalysisConfigPanel';
import AISettingsPanel from './AISettingsPanel';
import ListingsSummaryPanel from './ListingsSummaryPanel';
import AICostsPanel from './AICostsPanel';
import ReportsPanel from './ReportsPanel';
import {
  CoverSection,
  MarketTrendsSection,
  OnMarketCompsSection,
  RecentlySoldCompsSection,
  SummaryPricingSection,
  ListingPerformanceSection,
} from './sections';
import RecommendationsTextEditor from './sections/RecommendationsTextEditor';
import ValuationHistorySection from './sections/ValuationHistorySection';
import CustomSectionEditor from './CustomSectionEditor';
import {
  updateSection,
  toggleSection,
  resetSection,
  addCustomSection,
  deleteCustomSection,
  getPreview,
  getShareablePreview,
  getClientPreview,
  finalizeDraft,
  // regenerateWithExclusions,
  refreshAnalysis,
  regenerateCommentary,
  regenerateMarketingCommentary,
  generateMarketingUpdate,
  updateHiddenCharts,
} from '../../services/reportApi';

// Convert sections object to array format for SectionList - each section has an id and content
function sectionsToArray(sectionsObj) {
  if (!sectionsObj) return [];
  return Object.entries(sectionsObj).map(([id, section]) => ({
    id,
    ...section,
  }));
}

// Placement anchors offered for custom sections (Thank You is excluded — the
// default placement already puts customs just before it)
const PLACEMENT_LABELS = {
  tableOfContents: 'Table of Contents',
  about: 'About',
  marketTrends: 'Market Trends',
  onMarketComps: 'On Market Comps',
  recentlySoldComps: 'Recently Sold',
  summaryPricing: 'Valuation Summary',
  strategy: 'Services',
  listingPerformance: 'Listing Performance',
  valuationHistory: 'Valuation History',
  recommendations: 'Recommendations',
};
const VALUATION_PLACEMENTS = ['tableOfContents', 'about', 'marketTrends', 'onMarketComps', 'recentlySoldComps', 'summaryPricing', 'strategy'];
const MARKETING_PLACEMENTS = ['tableOfContents', 'listingPerformance', 'marketTrends', 'onMarketComps', 'recentlySoldComps', 'summaryPricing', 'valuationHistory', 'recommendations'];

function ReportEditor({ draft, onDraftChange, onFinalized, userData }) {
  const [selectedSection, setSelectedSection] = useState('draftSettings'); // Default to draft settings
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [shareableHtml, setShareableHtml] = useState('');
  const [clientHtml, setClientHtml] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isShareableLoading, setIsShareableLoading] = useState(false);
  const [isClientLoading, setIsClientLoading] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [reportsRefreshKey, setReportsRefreshKey] = useState(0);
  const [error, setError] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [shareableError, setShareableError] = useState(null);
  const [clientError, setClientError] = useState(null);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [isApplyingExclusions] = useState(false);
  const [hasDraftChanges, setHasDraftChanges] = useState(false);
  const [hasGuidelinesChanges, setHasGuidelinesChanges] = useState(false);
  const [pendingEditedComps, setPendingEditedComps] = useState(undefined); // undefined = no changes, array = edited comps
  const [excludedListings, setExcludedListings] = useState([]); // full exclusion list, synced from draft on load

  // Track current values from panels (for intelligent refresh)
  const [currentSubjectAircraft, setCurrentSubjectAircraft] = useState(null);
  const [currentAnalysisConfig, setCurrentAnalysisConfig] = useState(null);
  const [hasAnalysisConfigChanges, setHasAnalysisConfigChanges] = useState(false);
  const [currentGuidelines, setCurrentGuidelines] = useState(null);
  const [pendingSalesCompExclusions, setPendingSalesCompExclusions] = useState(null); // null = no changes, array = pending exclusions
  const [pendingNoPriceListings, setPendingNoPriceListings] = useState(null); // null = no changes, array = selected no-price listing IDs
  const [resetKey, setResetKey] = useState(0); // Incremented to trigger reset in child panels

  const draftId = draft?.draftId;
  const isMarketingUpdate = draft?.reportType === 'marketing_update';

  // Convert sections object to array
  const sectionsArray = useMemo(() => sectionsToArray(draft?.sections), [draft?.sections]);

  // Exclusion arrays from draft document (Firestore field names)
  const draftExcludedListingRegs = useMemo(() => {
    const regs = draft?.excludedListings ?? [];
    if (regs.length > 0) return regs;
    // Fallback: derive from listingsSummary if excludedListings array is empty
    const userExcluded = (draft?.listingsSummary?.excludedListings || [])
      .filter(l => l.reason === 'user_excluded')
      .map(l => (l.reg || l.registration || '').toUpperCase())
      .filter(Boolean);
    return userExcluded;
  }, [draft?.excludedListings, draft?.listingsSummary?.excludedListings]);
  const draftExcludedSalesCompRegs = useMemo(
    () => draft?.excludedSalesComps ?? [],
    [draft?.excludedSalesComps]
  );

  // Load print preview
  const loadPreview = useCallback(async () => {
    if (!draftId) return;
    try {
      setIsPreviewLoading(true);
      setPreviewError(null);
      const html = await getPreview(draftId);
      setPreviewHtml(html);
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setIsPreviewLoading(false);
    }
  }, [draftId]);

  // Load shareable/preparer preview
  const loadShareablePreview = useCallback(async () => {
    if (!draftId) return;
    try {
      setIsShareableLoading(true);
      setShareableError(null);
      const html = await getShareablePreview(draftId);
      setShareableHtml(html);
    } catch (err) {
      setShareableError(err.message);
    } finally {
      setIsShareableLoading(false);
    }
  }, [draftId]);

  // Load client preview (redacted view)
  const loadClientPreview = useCallback(async () => {
    if (!draftId) return;
    try {
      setIsClientLoading(true);
      setClientError(null);
      const html = await getClientPreview(draftId);
      setClientHtml(html);
    } catch (err) {
      setClientError(err.message);
    } finally {
      setIsClientLoading(false);
    }
  }, [draftId]);

  // Load both previews on draft change
  useEffect(() => {
    if (draft) {
      loadPreview();
      loadShareablePreview();
      loadClientPreview();
    }
  }, [draft, loadPreview, loadShareablePreview, loadClientPreview]);

  // Handle section toggle
  const handleToggleSection = async (sectionId, included) => {
    try {
      await toggleSection(draftId, sectionId, included);
      // Update local draft state
      if (onDraftChange) {
        onDraftChange((prev) => ({
          ...prev,
          sections: {
            ...prev.sections,
            [sectionId]: {
              ...prev.sections[sectionId],
              included,
            },
          },
        }));
      }
      loadPreview();
      loadShareablePreview();
      loadClientPreview();
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle section content update
  const handleUpdateSection = async (sectionId, content) => {
    try {
      setIsSaving(true);
      const response = await updateSection(draftId, sectionId, content);
      // Update local draft state with response or sent content
      if (onDraftChange) {
        const updatedContent = response?.content || { ...draft?.sections?.[sectionId]?.content, ...content };
        onDraftChange((prev) => ({
          ...prev,
          sections: {
            ...prev.sections,
            [sectionId]: {
              ...prev.sections[sectionId],
              content: updatedContent,
            },
          },
        }));
      }
      // Await the preview refresh
      await Promise.all([loadPreview(), loadShareablePreview(), loadClientPreview()]);
    } catch (err) {
      console.error('Update section error:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle photo change on a comp card — writes directly to Firestore and regenerates preview
  const handleCompPhotoChange = useCallback(async (reg, newPhotoUrl) => {
    const currentContent = draft?.sections?.onMarketComps?.content;
    if (!currentContent || !draftId) return;

    const regUpper = (reg || '').toUpperCase();
    const comparables = (currentContent.comparables || currentContent.listings || []).map(c => {
      const cReg = (c.reg || c.registration || '').toUpperCase();
      if (cReg === regUpper) {
        return { ...c, photoUrl: newPhotoUrl };
      }
      return c;
    });

    const updatedContent = { ...currentContent, comparables };

    // Update local state immediately
    if (onDraftChange) {
      onDraftChange((prev) => ({
        ...prev,
        sections: {
          ...prev.sections,
          onMarketComps: {
            ...prev.sections.onMarketComps,
            content: updatedContent,
          },
        },
      }));
    }

    // Persist directly to Firestore draft document and regenerate preview
    try {
      setIsSaving(true);
      const { doc: firestoreDoc, updateDoc } = await import('firebase/firestore');
      const db = (await import('../../firebase')).default;
      const draftRef = firestoreDoc(db, 'drafts', draftId);

      // Update comparables photoUrl in the draft's section content
      await updateDoc(draftRef, {
        'sections.onMarketComps.content.comparables': comparables,
      });

      // Also update editedComps if present so regeneration preserves the photo
      if (draft?.editedComps) {
        const updatedEditedComps = draft.editedComps.map(c => {
          const cReg = (c.reg || c.registration || '').toUpperCase();
          if (cReg === regUpper) {
            return { ...c, photoUrl: newPhotoUrl };
          }
          return c;
        });
        await updateDoc(draftRef, { editedComps: updatedEditedComps });
      }

      await Promise.all([loadPreview(), loadShareablePreview(), loadClientPreview()]);
    } catch (err) {
      console.error('Photo change save error:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [draft?.sections?.onMarketComps?.content, draft?.editedComps, draftId, onDraftChange, loadPreview, loadShareablePreview, loadClientPreview]);

  // Add a custom free-text section and jump to its editor
  const [isAddingSection, setIsAddingSection] = useState(false);
  const handleAddCustomSection = async () => {
    if (!draftId || isAddingSection) return;
    try {
      setIsAddingSection(true);
      setError(null);
      // Number default titles so multiple sections are distinguishable
      const customCount = Object.keys(draft?.sections || {}).filter((id) => id.startsWith('custom_')).length;
      const title = customCount > 0 ? `Custom Section ${customCount + 1}` : 'Custom Section';
      const response = await addCustomSection(draftId, { title });
      if (response?.sectionId && onDraftChange) {
        onDraftChange((prev) => ({
          ...prev,
          sections: {
            ...prev.sections,
            [response.sectionId]: response.section,
          },
        }));
        setSelectedSection(response.sectionId);
      }
    } catch (err) {
      console.error('Add custom section error:', err);
      setError(err.message);
    } finally {
      setIsAddingSection(false);
    }
    // Refresh previews in the background — don't keep the Add button locked
    Promise.all([loadPreview(), loadShareablePreview(), loadClientPreview()]);
  };

  // Delete a custom section
  const handleDeleteCustomSection = async (sectionId) => {
    try {
      setIsSaving(true);
      setError(null);
      await deleteCustomSection(draftId, sectionId);
      if (onDraftChange) {
        onDraftChange((prev) => {
          const sections = { ...prev.sections };
          delete sections[sectionId];
          return { ...prev, sections };
        });
      }
      setSelectedSection('draftSettings');
      await Promise.all([loadPreview(), loadShareablePreview(), loadClientPreview()]);
    } catch (err) {
      console.error('Delete custom section error:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle section reset
  const handleResetSection = async (sectionId) => {
    try {
      setIsSaving(true);
      const resetData = await resetSection(draftId, sectionId);
      // Update local draft state with original content
      if (onDraftChange) {
        onDraftChange((prev) => ({
          ...prev,
          sections: {
            ...prev.sections,
            [sectionId]: {
              ...prev.sections[sectionId],
              content: resetData.content || prev.sections[sectionId].originalContent,
            },
          },
        }));
      }
      loadPreview();
      loadShareablePreview();
      loadClientPreview();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle finalization
  const handleFinalize = async () => {
    try {
      setIsFinalizing(true);
      const result = await finalizeDraft(draftId);
      setShowFinalizeDialog(false);
      if (onFinalized) {
        onFinalized(result);
      }
      // Navigate to reports panel and trigger refresh to show the new report
      setReportsRefreshKey((k) => k + 1);
      setSelectedSection('reports');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsFinalizing(false);
    }
  };

  // Queue exclusions from iframe chart selections (applied on manual refresh)
  const queueExclusionsFromIframe = useCallback((newExcludedIds) => {
    if (!newExcludedIds || newExcludedIds.length === 0) return;

    setExcludedListings((prev) => {
      const combined = [...new Set([...prev, ...newExcludedIds])];
      return combined;
    });
  }, []);

  // Listen for excludePoints events from iframe - queue for manual refresh
  useEffect(() => {
    const handleExcludePointsEvent = (event) => {
      const excludedIds = event.detail?.excludedIds || [];
      if (excludedIds.length > 0) {
        queueExclusionsFromIframe(excludedIds);
      }
    };

    window.addEventListener('excludePoints', handleExcludePointsEvent);
    return () => {
      window.removeEventListener('excludePoints', handleExcludePointsEvent);
    };
  }, [queueExclusionsFromIframe]);

  // Mark a listing for restoration (will be applied on next refresh)
  const handleRestoreExclusion = useCallback((registrationToRestore) => {
    const regUpper = registrationToRestore?.toUpperCase();
    const listing = (draft?.listingsSummary?.excludedListings || [])
      .find(l => (l.reg || l.registration)?.toUpperCase() === regUpper);

    // Only block reverting system exclusions (like missing_price) — allow user-excluded and pending exclusions
    if (listing && listing.reason !== 'user_excluded') return;

    // Remove from excluded list (works for both persisted and pending exclusions)
    setExcludedListings((prev) => prev.filter(r => r.toUpperCase() !== regUpper));
  }, [draft?.listingsSummary?.excludedListings]);

  // Mark all user-excluded listings for restoration (will be applied on next refresh)
  const handleRevertAllExclusions = useCallback(() => {
    const userExcludedRegs = (draft?.listingsSummary?.excludedListings || [])
      .filter(listing => listing.reason === 'user_excluded' && (listing.price_usd || listing.price))
      .map(listing => (listing.reg || '').toUpperCase());

    setExcludedListings((prev) => prev.filter(r => !userExcludedRegs.includes(r.toUpperCase())));
  }, [draft?.listingsSummary?.excludedListings]);

  // Toggle chart visibility (immediately persisted, no refresh needed for data)
  const handleToggleChart = useCallback(async (chartId) => {
    if (!draftId) return;

    // Use functional update to get latest state
    const currentHidden = draft?.hiddenCharts || [];
    const isCurrentlyHidden = currentHidden.includes(chartId);
    const newHidden = isCurrentlyHidden
      ? currentHidden.filter(id => id !== chartId)  // Show
      : [...currentHidden, chartId];                 // Hide

    try {
      const response = await updateHiddenCharts(draftId, newHidden);

      // Update local state with API response
      if (onDraftChange && response.hiddenCharts !== undefined) {
        onDraftChange((prev) => ({
          ...prev,
          hiddenCharts: response.hiddenCharts,
        }));
      }

      // Refresh preview to show updated charts
      await Promise.all([loadPreview(), loadShareablePreview(), loadClientPreview()]);
    } catch (err) {
      console.error('Failed to update hidden charts:', err);
      setError(err.message);
    }
  }, [draftId, draft?.hiddenCharts, onDraftChange, loadPreview, loadShareablePreview, loadClientPreview]);

  // Handle sales comp exclusion toggle (local state only, applied on refresh)
  const handleToggleSalesCompExclusion = useCallback((reg) => {
    if (!reg) return;

    const regUpper = reg.toUpperCase();
    // Use pending state if exists, otherwise start from draft state
    const currentExcluded = pendingSalesCompExclusions ?? draftExcludedSalesCompRegs ?? [];
    const newExcluded = currentExcluded.includes(regUpper)
      ? currentExcluded.filter(r => r !== regUpper)  // Re-include
      : [...currentExcluded, regUpper];              // Exclude

    setPendingSalesCompExclusions(newExcluded);
  }, [pendingSalesCompExclusions, draftExcludedSalesCompRegs]);

  // Handle on-market comp exclusion toggle (local state only, applied on refresh)
  const handleToggleListingExclusion = useCallback((reg) => {
    if (!reg) return;
    const regUpper = reg.toUpperCase();

    setExcludedListings((prev) =>
      prev.includes(regUpper)
        ? prev.filter(r => r !== regUpper)
        : [...prev, regUpper]
    );
  }, []);

  // Handle on-market comps change (local state only, submitted on refresh)
  const handleCompsChange = useCallback((editedComps) => {
    setPendingEditedComps(editedComps);
  }, []);

  // Reset to AI-selected comparables and only un-exclude listings that were in the AI selection
  const handleResetCompsToAI = useCallback(() => {
    const originalComps = draft?.sections?.onMarketComps?.originalContent?.comparables
      || draft?.sections?.onMarketComps?.originalContent?.listings
      || [];
    setPendingEditedComps(originalComps);

    // Get the registrations from the AI's original selection
    const aiSelectedRegs = new Set(
      originalComps
        .map(comp => (comp.reg || comp.registration || '').toUpperCase())
        .filter(Boolean)
    );

    // Only remove exclusions for listings that were in the AI's original selection
    // Exclusions for listings NOT in the AI selection remain unchanged
    setExcludedListings((prev) =>
      prev.filter(reg => !aiSelectedRegs.has(reg.toUpperCase()))
    );
  }, [draft?.sections?.onMarketComps?.originalContent]);

  // Discard all pending changes and reset to draft values
  const handleDiscardChanges = useCallback(() => {
    // Reset local state controlled by ReportEditor
    setPendingEditedComps(undefined);
    setExcludedListings(draftExcludedListingRegs);
    setPendingSalesCompExclusions(null);

    // Increment resetKey to trigger reset in child panels (SubjectAircraftPanel, AnalysisConfigPanel, AISettingsPanel)
    setResetKey((k) => k + 1);
  }, [draftExcludedListingRegs]);

  // Reset pending comps when draft changes (e.g., after refresh)
  useEffect(() => {
    setPendingEditedComps(undefined);
  }, [draft?.draftId, draft?.sections?.onMarketComps?.content]);

  // Sync local exclusion list from draft (on load and after refresh)
  useEffect(() => {
    setExcludedListings(draftExcludedListingRegs);
  }, [draftExcludedListingRegs]);

  // Reset pending sales comp exclusions when draft changes
  useEffect(() => {
    setPendingSalesCompExclusions(null);
  }, [draft?.draftId, draftExcludedSalesCompRegs]);

  // Reset draft changes flag when draft is refreshed
  useEffect(() => {
    setHasDraftChanges(false);
  }, [draft?.draftId, draft?.subjectAircraft]);

  // Reset guidelines changes flag when draft guidelines are updated
  useEffect(() => {
    setHasGuidelinesChanges(false);
  }, [draft?.draftId, draft?.customGuidelines]);

  // Check if there are pending comp changes (compare registrations, not just presence)
  const hasCompChanges = useMemo(() => {
    if (pendingEditedComps === undefined) return false;
    const draftComps = draft?.sections?.onMarketComps?.content?.comparables
      || draft?.sections?.onMarketComps?.content?.listings
      || [];
    if (pendingEditedComps.length !== draftComps.length) return true;
    const pendingRegs = pendingEditedComps.map(c => (c.reg || c.registration || '').toUpperCase()).sort();
    const draftRegs = draftComps.map(c => (c.reg || c.registration || '').toUpperCase()).sort();
    return JSON.stringify(pendingRegs) !== JSON.stringify(draftRegs);
  }, [pendingEditedComps, draft?.sections?.onMarketComps?.content]);

  // Check if there are pending exclusion changes
  const hasExclusionChanges = JSON.stringify(excludedListings.map(r => r.toUpperCase()).sort()) !== JSON.stringify(draftExcludedListingRegs.map(r => r.toUpperCase()).sort());

  // Check if there are pending sales comp exclusion changes
  const hasSalesCompExclusionChanges = pendingSalesCompExclusions !== null;

  // Check if there are pending no-price listing changes
  const hasNoPriceChanges = pendingNoPriceListings !== null &&
    JSON.stringify(pendingNoPriceListings.sort()) !== JSON.stringify((draft?.includedNoPriceListings ?? []).sort());

  // Compute effective on-market comps content (with pending changes applied)
  const effectiveOnMarketCompsContent = useMemo(() => {
    const currentContent = draft?.sections?.onMarketComps?.content;
    if (!currentContent) return null;
    if (pendingEditedComps === undefined) return currentContent;
    return {
      ...currentContent,
      comparables: pendingEditedComps,
    };
  }, [draft?.sections?.onMarketComps?.content, pendingEditedComps]);

  const effectiveExcludedListings = excludedListings;

  // Determine what type of changes exist
  const hasDataChanges = hasDraftChanges || hasAnalysisConfigChanges || hasCompChanges || hasExclusionChanges || hasSalesCompExclusionChanges || hasNoPriceChanges;
  const hasAnyChanges = hasDataChanges || hasGuidelinesChanges;

  // Single intelligent refresh handler
  // force = true regenerates market data even with no pending changes (force regenerate button)
  const runRefresh = useCallback(async (force = false) => {
    if (!draftId || (!hasAnyChanges && !force)) return;

    try {
      setIsRefreshing(true);
      setError(null);

      if (hasDataChanges || force) {
        // Data changed (with or without guidelines) - full refresh
        const params = {
          subjectRegistration: currentSubjectAircraft?.registration,
          subjectManufacturer: currentSubjectAircraft?.manufacturer,
          subjectModel: currentSubjectAircraft?.model,
          subjectYear: currentSubjectAircraft?.year,
          subjectHours: currentSubjectAircraft?.hours,
          yearRange: currentAnalysisConfig?.yearRange ?? draft?.analysisConfig?.yearRange,
          subjectAircraftDetails: currentSubjectAircraft?.additionalDetails,
          excludedListings,
          periodDays: currentAnalysisConfig?.periodDays ?? draft?.analysisConfig?.periodDays,
        };

        // Include guidelines if they've changed
        if (hasGuidelinesChanges && currentGuidelines) {
          params.customGuidelines = currentGuidelines;
        }

        // Include edited comps if any
        if (pendingEditedComps !== undefined) {
          params.editedComps = pendingEditedComps;
        }

        // Include sales comp exclusions if changed
        if (pendingSalesCompExclusions !== null) {
          params.excludedSalesComps = pendingSalesCompExclusions;
        }

        // Include no-price listings if changed
        if (pendingNoPriceListings !== null) {
          params.includedNoPriceListings = pendingNoPriceListings;
        }

        // Detect if core parameters changed (triggers backend to discard stale comps)
        const fundamentalsChanged =
          (currentSubjectAircraft?.manufacturer || '') !== (draft?.subjectAircraft?.manufacturer || '') ||
          (currentSubjectAircraft?.model || '') !== (draft?.subjectAircraft?.model || '') ||
          currentSubjectAircraft?.year !== draft?.subjectAircraft?.year ||
          (currentAnalysisConfig?.yearRange ?? draft?.analysisConfig?.yearRange) !== draft?.analysisConfig?.yearRange ||
          (currentAnalysisConfig?.periodDays ?? draft?.analysisConfig?.periodDays) !== draft?.analysisConfig?.periodDays;

        if (fundamentalsChanged) {
          params.fundamentalsChanged = true;
        }

        const response = await refreshAnalysis(draftId, params);

        if (response?.draft && onDraftChange) {
          onDraftChange(response.draft);
        }

        // Clear pending state after successful refresh
        // (excludedListings will sync from draft via useEffect)
        setPendingEditedComps(undefined);
        setPendingSalesCompExclusions(null);
        setPendingNoPriceListings(null);
      } else if (hasGuidelinesChanges && currentGuidelines) {
        // Only guidelines changed - regenerate commentary only
        if (isMarketingUpdate) {
          const response = await regenerateMarketingCommentary(draftId, currentGuidelines);
          if (response.success && onDraftChange) {
            onDraftChange((prev) => {
              const updated = {
                ...prev,
                customGuidelines: response.guidelinesUsed,
              };
              if (response.promptUsed) {
                updated.pass1Prompt = response.promptUsed;
              }
              // Apply marketing commentary updates
              if (response.commentary) {
                updated.sections = { ...prev.sections };
                if (response.commentary.listingPerformanceCommentary && updated.sections.listingPerformance) {
                  updated.sections.listingPerformance = {
                    ...updated.sections.listingPerformance,
                    content: {
                      ...updated.sections.listingPerformance.content,
                      commentary: response.commentary.listingPerformanceCommentary,
                    },
                  };
                }
                if (response.commentary.recommendations && updated.sections.recommendations) {
                  updated.sections.recommendations = {
                    ...updated.sections.recommendations,
                    content: {
                      ...updated.sections.recommendations.content,
                      text: response.commentary.recommendations,
                    },
                  };
                }
              }
              return updated;
            });
          }
        } else {
          const response = await regenerateCommentary(draftId, currentGuidelines);
          if (response.success && onDraftChange) {
            onDraftChange((prev) => {
              const updated = {
                ...prev,
                customGuidelines: response.guidelinesUsed,
                pass1Prompt: response.pass1Prompt,
                pass2Prompt: response.pass2Prompt,
              };
              if (response.commentary) {
                updated.sections = { ...prev.sections };
                Object.entries(response.commentary).forEach(([sectionId, content]) => {
                  if (updated.sections[sectionId]) {
                    updated.sections[sectionId] = {
                      ...updated.sections[sectionId],
                      content,
                    };
                  }
                });
              }
              return updated;
            });
          }
        }
      }

      // Refresh previews after any change
      await Promise.all([loadPreview(), loadShareablePreview(), loadClientPreview()]);
    } catch (err) {
      console.error('Refresh failed:', err);
      setError(err.message);
    } finally {
      setIsRefreshing(false);
    }
  }, [draftId, hasAnyChanges, hasDataChanges, hasGuidelinesChanges, isMarketingUpdate, currentSubjectAircraft, currentAnalysisConfig, currentGuidelines, pendingEditedComps, excludedListings, pendingSalesCompExclusions, pendingNoPriceListings, draft?.subjectAircraft, draft?.analysisConfig, onDraftChange, loadPreview, loadShareablePreview, loadClientPreview]);

  const handleRefreshClick = useCallback(() => runRefresh(false), [runRefresh]);
  const handleForceRefresh = useCallback(() => runRefresh(true), [runRefresh]);

  // Marketing update regeneration
  const handleRegenerateMarketing = useCallback(async () => {
    if (!draftId || !isMarketingUpdate) return;
    try {
      setIsRefreshing(true);
      setError(null);
      const listingId = draft?.sections?.listingPerformance?.content?.listingId;
      if (!listingId) {
        setError('Missing listing ID');
        return;
      }
      const params = {
        draftId,
        listingId,
        analysisConfig: {
          periodDays: currentAnalysisConfig?.periodDays ?? draft?.analysisConfig?.periodDays ?? 730,
          yearRange: currentAnalysisConfig?.yearRange ?? draft?.analysisConfig?.yearRange ?? 2,
        },
        excludedListings,
        excludedSalesComps: pendingSalesCompExclusions ?? draft?.excludedSalesComps ?? [],
      };
      // Include edited comps if any (backend preserves stored edits when not sent)
      if (pendingEditedComps !== undefined) {
        params.editedComps = pendingEditedComps;
      }
      if (hasGuidelinesChanges && currentGuidelines) {
        params.customGuidelines = currentGuidelines;
      }
      // Pass valuation settings if available
      if (currentSubjectAircraft?.additionalDetails || draft?.subjectAircraft?.additionalDetails) {
        params.additionalDetails = currentSubjectAircraft?.additionalDetails || draft?.subjectAircraft?.additionalDetails;
      }
      if (draft?.valuationGuidelines) {
        params.valuationGuidelines = draft.valuationGuidelines;
      }
      const response = await generateMarketingUpdate(params);
      if (response && onDraftChange) {
        onDraftChange(response);
      }
      setPendingEditedComps(undefined);
      setPendingSalesCompExclusions(null);
      await Promise.all([loadPreview(), loadShareablePreview(), loadClientPreview()]);
    } catch (err) {
      console.error('Marketing regeneration failed:', err);
      setError(err.message);
    } finally {
      setIsRefreshing(false);
    }
  }, [draftId, isMarketingUpdate, draft?.sections?.listingPerformance?.content?.listingId, draft?.analysisConfig, draft?.excludedSalesComps, currentAnalysisConfig, currentSubjectAircraft?.additionalDetails, draft?.subjectAircraft?.additionalDetails, draft?.valuationGuidelines, excludedListings, pendingEditedComps, pendingSalesCompExclusions, hasGuidelinesChanges, currentGuidelines, onDraftChange, loadPreview, loadShareablePreview, loadClientPreview]);

  // Get current section data
  const currentSection = draft?.sections?.[selectedSection];

  // Render editor based on section type
  const renderEditor = () => {
    if (!selectedSection || !currentSection) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <Typography>Select an editable section to edit</Typography>
        </div>
      );
    }

    // Custom (user-created) section editor
    if (selectedSection.startsWith('custom_')) {
      const placementIds = isMarketingUpdate ? MARKETING_PLACEMENTS : VALUATION_PLACEMENTS;
      return (
        <CustomSectionEditor
          content={currentSection.content}
          placementOptions={placementIds.map((id) => ({ id, label: PLACEMENT_LABELS[id] || id }))}
          onSave={(content) => handleUpdateSection(selectedSection, content)}
          onDelete={() => handleDeleteCustomSection(selectedSection)}
          isSaving={isSaving}
        />
      );
    }

    // Cover page editor
    if (selectedSection === 'cover') {
      return (
        <CoverSection
          content={currentSection.content}
          draft={draft}
          onSave={(content) => handleUpdateSection('cover', { ...content, dateFormat: userData?.dateFormat || 'MM/DD/YYYY' })}
          onReset={() => handleResetSection('cover')}
          isSaving={isSaving}
        />
      );
    }

    // Always show section viewers for data-driven sections
    if (selectedSection === 'marketTrends') {
      return (
        <MarketTrendsSection
          content={currentSection.content}
          listingsSummary={draft?.listingsSummary}
          hiddenCharts={draft?.hiddenCharts || []}
          onToggleChart={handleToggleChart}
          onTrendPointClick={(chartType) => {
            // TODO: Open source listings modal
          }}
          onRestoreExclusion={handleRestoreExclusion}
          isApplyingExclusions={isApplyingExclusions}
          onSave={(content) => handleUpdateSection('marketTrends', content)}
          onReset={() => handleResetSection('marketTrends')}
          isSaving={isSaving}
        />
      );
    }
    if (selectedSection === 'onMarketComps') {
      return (
        <OnMarketCompsSection
          content={effectiveOnMarketCompsContent}
          originalContent={currentSection.originalContent || currentSection.content}
          onCompsChange={handleCompsChange}
          onPhotoChange={handleCompPhotoChange}
          onResetToAI={handleResetCompsToAI}
          excludedListings={effectiveExcludedListings}
          onToggleExclusion={handleToggleListingExclusion}
          isUpdating={false}
          hasLocalChanges={hasCompChanges || hasExclusionChanges}
          subjectRegistration={isMarketingUpdate ? (draft?.subjectAircraft?.registration || draft?.aircraftId) : null}
        />
      );
    }
    if (selectedSection === 'recentlySoldComps') {
      return (
        <RecentlySoldCompsSection
          content={currentSection.content}
          allSales={draft?.rawData?.allSales}
          excludedSalesComps={pendingSalesCompExclusions ?? draftExcludedSalesCompRegs ?? []}
          onToggleExclusion={handleToggleSalesCompExclusion}
        />
      );
    }

    if (selectedSection === 'listingPerformance') {
      return (
        <ListingPerformanceSection
          content={currentSection.content}
          hiddenCharts={draft?.hiddenCharts || []}
          onToggleChart={handleToggleChart}
          onSave={(content) => handleUpdateSection('listingPerformance', content)}
          onReset={() => handleResetSection('listingPerformance')}
          onToggleKpi={async (hiddenKpis) => {
            try {
              await updateSection(draftId, 'listingPerformance', { hiddenKpis });
              if (onDraftChange) {
                onDraftChange((prev) => ({
                  ...prev,
                  sections: {
                    ...prev.sections,
                    listingPerformance: {
                      ...prev.sections.listingPerformance,
                      content: {
                        ...prev.sections.listingPerformance.content,
                        hiddenKpis,
                      },
                    },
                  },
                }));
              }
            } catch (err) {
              console.error('Failed to toggle KPI:', err);
              setError(err.message);
            }
          }}
          onSaveCustomLeads={async (customLeads) => {
            try {
              await updateSection(draftId, 'listingPerformance', { customLeads });
              if (onDraftChange) {
                onDraftChange((prev) => ({
                  ...prev,
                  sections: {
                    ...prev.sections,
                    listingPerformance: {
                      ...prev.sections.listingPerformance,
                      content: {
                        ...prev.sections.listingPerformance.content,
                        customLeads,
                      },
                    },
                  },
                }));
              }
            } catch (err) {
              console.error('Failed to save custom leads:', err);
              setError(err.message);
            }
          }}
          isSaving={isSaving}
        />
      );
    }

    if (selectedSection === 'valuationHistory') {
      return (
        <ValuationHistorySection
          content={currentSection.content}
          originalContent={currentSection.originalContent}
          onSave={(content) => handleUpdateSection('valuationHistory', content)}
          onReset={() => handleResetSection('valuationHistory')}
          isSaving={isSaving}
        />
      );
    }

    if (selectedSection === 'recommendations') {
      // Marketing updates use a single text field; valuations use ContentBlock[]
      if (isMarketingUpdate || typeof currentSection.content?.text === 'string') {
        return (
          <RecommendationsTextEditor
            content={currentSection.content}
            originalContent={currentSection.originalContent}
            onSave={(content) => handleUpdateSection(selectedSection, content)}
            onReset={() => handleResetSection(selectedSection)}
            isSaving={isSaving}
          />
        );
      }
      return (
        <DynamicSectionsEditor
          title="Recommendations"
          sections={currentSection.content?.sections || []}
          onSave={(content) => handleUpdateSection(selectedSection, content)}
          onReset={() => handleResetSection(selectedSection)}
          isSaving={isSaving}
        />
      );
    }

    if (!currentSection.editable) {
      // Default message for other non-editable sections
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <Typography>This section is not editable (data-driven)</Typography>
        </div>
      );
    }

    if (selectedSection === 'about') {
      return (
        <DynamicSectionsEditor
          title="About Lone Mountain"
          sections={currentSection.content?.sections || []}
          specialFields={{
            subtitle: currentSection.content?.subtitle || '',
          }}
          specialFieldsConfig={[
            { name: 'subtitle', label: 'Subtitle', type: 'input' },
          ]}
          onSave={(content) => handleUpdateSection(selectedSection, content)}
          onReset={() => handleResetSection(selectedSection)}
          isSaving={isSaving}
        />
      );
    }

    if (selectedSection === 'summaryPricing') {
      return (
        <SummaryPricingSection
          content={currentSection.content}
          onSave={(content) => handleUpdateSection('summaryPricing', content)}
          onReset={() => handleResetSection('summaryPricing')}
          isSaving={isSaving}
        />
      );
    }

    if (selectedSection === 'strategy') {
      return (
        <DynamicSectionsEditor
          title="Brokerage Services"
          sections={currentSection.content?.sections || []}
          specialFields={{
            brokerageFee: currentSection.content?.brokerageFee || '',
          }}
          specialFieldsConfig={[
            { name: 'brokerageFee', label: 'Brokerage Fee', type: 'input' },
          ]}
          onSave={(content) => handleUpdateSection(selectedSection, content)}
          onReset={() => handleResetSection(selectedSection)}
          isSaving={isSaving}
        />
      );
    }

    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>Editor not available for this section type</Typography>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {error && (
        <Alert color="red" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-6">
          <div>
            <div className="flex items-center gap-2">
              <Typography variant="h5" color="blue-gray">
                Report Editor
              </Typography>
              <Chip
                size="sm"
                value={isMarketingUpdate ? 'Marketing Update' : 'Market Valuation'}
                color={isMarketingUpdate ? 'purple' : 'blue'}
                variant="ghost"
              />
              <Tooltip content="Force regenerate">
                <IconButton
                  variant="text"
                  size="sm"
                  color="amber"
                  onClick={isMarketingUpdate ? handleRegenerateMarketing : handleForceRefresh}
                  disabled={isRefreshing}
                  className="rounded-full"
                >
                  {isRefreshing ? <Spinner className="h-3.5 w-3.5" /> : <BoltIcon className="h-3.5 w-3.5" />}
                </IconButton>
              </Tooltip>
            </div>
            <Typography variant="small" color="gray">
              {draft?.customerName} - {draft?.aircraftModel}{(draft?.aircraftId || draft?.aircraftRegistration || draft?.subjectAircraft?.registration) ? ` (${draft.aircraftId || draft.aircraftRegistration || draft.subjectAircraft.registration})` : ''}
            </Typography>
            {draft?.onBehalfOf && draft?.preparer?.name && (
              <Typography variant="small" className="text-xs text-amber-700 font-medium">
                On behalf of {draft.preparer.name}
              </Typography>
            )}
          </div>
          {!isMarketingUpdate && (draft?.sections?.summaryPricing?.content?.estimatedValue || draft?.sections?.summaryPricing?.content?.estimated_value) && (
            <div className="px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <Typography variant="small" color="blue" className="text-xs font-medium">
                Estimated Value
              </Typography>
              <Typography variant="h6" color="blue">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
                  draft?.sections?.summaryPricing?.content?.estimatedValue || draft?.sections?.summaryPricing?.content?.estimated_value
                )}
              </Typography>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasAnyChanges && (
            <Button
              variant="outlined"
              color="red"
              className="flex items-center gap-2"
              onClick={handleDiscardChanges}
              disabled={isRefreshing}
            >
              <XMarkIcon className="h-5 w-5" />
              Discard
            </Button>
          )}
          {isMarketingUpdate ? (
            <Button
              variant={hasAnyChanges ? 'filled' : 'outlined'}
              color={hasAnyChanges ? 'blue' : 'gray'}
              className="flex items-center gap-2"
              onClick={hasGuidelinesChanges && !hasDataChanges ? handleRefreshClick : handleRegenerateMarketing}
              disabled={!hasAnyChanges || isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <Spinner className="h-4 w-4" />
                  Regenerating...
                </>
              ) : hasAnyChanges ? (
                <>
                  <ArrowPathIcon className="h-5 w-5" />
                  {hasDataChanges ? 'Regenerate' : 'Update Commentary'}
                </>
              ) : (
                <>
                  <ArrowPathIcon className="h-5 w-5" />
                  No Changes
                </>
              )}
            </Button>
          ) : (
            <Button
              variant={hasAnyChanges ? 'filled' : 'outlined'}
              color={hasAnyChanges ? 'blue' : 'gray'}
              className="flex items-center gap-2"
              onClick={handleRefreshClick}
              disabled={!hasAnyChanges || isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <Spinner className="h-4 w-4" />
                  Refreshing...
                </>
              ) : hasAnyChanges ? (
                <>
                  <ArrowPathIcon className="h-5 w-5" />
                  {hasDataChanges ? 'Refresh Analysis' : 'Update Commentary'}
                </>
              ) : (
                <>
                  <ArrowPathIcon className="h-5 w-5" />
                  No Changes
                </>
              )}
            </Button>
          )}
          <Button
            className="flex items-center gap-2"
            onClick={() => setShowFinalizeDialog(true)}
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            Create Version
          </Button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
        {/* Section list - left sidebar (narrow) */}
        <div className="col-span-2 overflow-auto">
          <SectionList
            sections={sectionsArray}
            selectedSection={selectedSection}
            onSelectSection={setSelectedSection}
            onToggleSection={handleToggleSection}
            onAddCustomSection={handleAddCustomSection}
            isAddingSection={isAddingSection}
            hasDraftChanges={hasDraftChanges || hasAnalysisConfigChanges}
            hasCompChanges={hasCompChanges}
            hasGuidelinesChanges={hasGuidelinesChanges}
            hasExclusionChanges={hasExclusionChanges}
            pendingExclusionsCount={Math.abs(excludedListings.length - draftExcludedListingRegs.length)}
            hasSalesCompExclusionChanges={hasSalesCompExclusionChanges}
            isLocked={isRefreshing}
            periodDays={draft?.analysisConfig?.periodDays}
            reportCount={draft?.reportCount || 0}
            isMarketingUpdate={isMarketingUpdate}
          />
        </div>

        {/* Editor - center (narrow) */}
        <div className="col-span-4 overflow-auto relative">
          {/* Loading overlay when refreshing */}
          {isRefreshing && (
            <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <Spinner className="h-8 w-8" />
                <Typography variant="small" color="blue-gray">
                  Refreshing analysis...
                </Typography>
              </div>
            </div>
          )}
          {/* Settings panels - mounted to preserve form state */}
          <div className={selectedSection === 'draftSettings' ? '' : 'hidden'}>
            {isMarketingUpdate && (
              <Card className="mb-4">
                <CardBody className="p-4">
                  <Typography variant="h6" color="blue-gray" className="mb-3">
                    Listing Details
                  </Typography>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Registration', value: draft?.subjectAircraft?.registration || draft?.aircraftId || draft?.aircraftRegistration },
                      { label: 'Year', value: draft?.subjectAircraft?.year || draft?.aircraftYear },
                      { label: 'Manufacturer', value: draft?.subjectAircraft?.manufacturer || draft?.aircraftManufacturer },
                      { label: 'Model', value: draft?.subjectAircraft?.model || draft?.aircraftModel },
                      { label: 'Listing ID', value: draft?.sections?.listingPerformance?.content?.listingId },
                      { label: 'Customer', value: draft?.customerName },
                    ].filter((f) => f.value).map((f) => (
                      <div key={f.label}>
                        <Typography variant="small" className="text-xs text-gray-500">{f.label}</Typography>
                        <Typography variant="small" className="font-medium">{f.value}</Typography>
                      </div>
                    ))}
                  </div>
                  {/* Additional Details - editable, sent on regenerate */}
                  <div className="mt-3">
                    <Typography variant="small" className="text-xs text-gray-500 mb-1">Additional Aircraft Details</Typography>
                    <textarea
                      value={currentSubjectAircraft?.additionalDetails ?? draft?.subjectAircraft?.additionalDetails ?? ''}
                      onChange={(e) => {
                        setCurrentSubjectAircraft((prev) => ({
                          ...(prev || draft?.subjectAircraft || {}),
                          additionalDetails: e.target.value,
                        }));
                        setHasDraftChanges(true);
                      }}
                      placeholder="e.g., FIKI, GTS, new paint 2024, fresh annual..."
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-y"
                    />
                  </div>
                </CardBody>
              </Card>
            )}
            <DelegationPanel
              draft={draft}
              onDraftChange={onDraftChange}
              onError={setError}
            />
            {!isMarketingUpdate && (
              <SubjectAircraftPanel
                draft={draft}
                onValuesChange={setCurrentSubjectAircraft}
                onDataChanged={setHasDraftChanges}
                resetKey={resetKey}
              />
            )}
            <AnalysisConfigPanel
              draft={draft}
              onValuesChange={setCurrentAnalysisConfig}
              onDataChanged={setHasAnalysisConfigChanges}
              resetKey={resetKey}
            />
          </div>
          {/* Keep AISettingsPanel mounted to preserve form state */}
          <div className={selectedSection === 'aiSettings' ? 'h-full' : 'hidden'}>
            <AISettingsPanel
              draft={draft}
              onGuidelinesChange={setCurrentGuidelines}
              onHasChanges={setHasGuidelinesChanges}
              onError={setError}
              resetKey={resetKey}
              reportType={draft?.reportType}
            />
          </div>
          {selectedSection === 'aiCosts' && <AICostsPanel draft={draft} />}
          {selectedSection === 'listings' && (
            <ListingsSummaryPanel
              listingsSummary={draft?.listingsSummary}
              periodDays={draft?.analysisConfig?.periodDays}
              onRevertExclusion={handleRestoreExclusion}
              onRevertAllExclusions={handleRevertAllExclusions}
              effectiveExcludedListings={excludedListings}
              draftExcludedListingRegs={draftExcludedListingRegs}
              hasExclusionChanges={hasExclusionChanges}
              includedNoPriceListings={pendingNoPriceListings ?? draft?.includedNoPriceListings ?? []}
              onToggleNoPriceListing={(listingId) => {
                setPendingNoPriceListings(prev => {
                  const current = prev ?? draft?.includedNoPriceListings ?? [];
                  if (current.includes(listingId)) {
                    return current.filter(id => id !== listingId);
                  }
                  return [...current, listingId];
                });
              }}
            />
          )}
          {selectedSection === 'reports' && <ReportsPanel draftId={draftId} draft={draft} refreshKey={reportsRefreshKey} />}
          {selectedSection !== 'draftSettings' && selectedSection !== 'aiSettings' && selectedSection !== 'aiCosts' && selectedSection !== 'listings' && selectedSection !== 'reports' && renderEditor()}
        </div>

        {/* Preview - right (expanded) */}
        <div className="col-span-6 overflow-hidden">
          <PreviewPane
            htmlContent={previewHtml}
            shareableContent={shareableHtml}
            clientContent={clientHtml}
            isLoading={isPreviewLoading}
            isLoadingShareable={isShareableLoading}
            isLoadingClient={isClientLoading}
            isApplyingExclusions={isApplyingExclusions}
            error={previewError}
            errorShareable={shareableError}
            errorClient={clientError}
            onRefresh={loadPreview}
            onRefreshShareable={loadShareablePreview}
            onLoadClientPreview={loadClientPreview}
            selectedSection={selectedSection}
          />
        </div>
      </div>

      {/* Finalize confirmation dialog */}
      <Dialog open={showFinalizeDialog} handler={setShowFinalizeDialog}>
        <DialogHeader>Create Version</DialogHeader>
        <DialogBody>
          <Typography>
            This will create a shareable version of the report. You can
            continue editing the draft and create additional versions.
            Previous versions will remain accessible.
          </Typography>
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button
            variant="outlined"
            onClick={() => setShowFinalizeDialog(false)}
            disabled={isFinalizing}
          >
            Cancel
          </Button>
          <Button onClick={handleFinalize} loading={isFinalizing}>
            Create
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default ReportEditor;
